/**
 * The Android app's sign-in token (mobile phase M1).
 *
 * A browser signs in with an HttpOnly cookie that JavaScript never sees, and
 * nothing here runs for it. The app has no cookie — its pages load from
 * `https://localhost`, not the site — so it carries a token as
 * `Authorization: Bearer …` instead. That token is a year-long credential, so:
 *
 *  * **Android's encrypted storage, never `localStorage`.** `localStorage` in
 *    a WebView is a plain file. The token is written through the secure-storage
 *    plugin (installed in M2), whose JS is imported on native only — a browser
 *    never downloads it.
 *  * **Until then, memory only.** Without the plugin the token lives in this
 *    module and the session ends when the app is killed. Honest degradation —
 *    never a quiet fallback to a readable file.
 *  * **Read synchronously, written asynchronously.** Every request needs it,
 *    so it is cached here; `loadAuthToken()` fills the cache at boot.
 */

import { clientType, isNative } from "./index";

type SecureStore = {
  get(options: { key: string }): Promise<{ value: string | null }>;
  set(options: { key: string; value: string }): Promise<unknown>;
  remove(options: { key: string }): Promise<unknown>;
};

const TOKEN_KEY = "mydeck.session";
let token: string | null = null;

function secureStore(): SecureStore | undefined {
  const cap = (window as Window & { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
  return cap?.Plugins?.SecureStoragePlugin as SecureStore | undefined;
}

/**
 * The plugin appears on `Capacitor.Plugins` only once its JS has run
 * `registerPlugin`, so it is imported before the first read or write — once,
 * on native only. A failed import leaves the store absent: the memory-only path.
 *
 * Resolves to nothing on purpose; callers read the plugin with `secureStore()`
 * afterwards. A Capacitor plugin is a Proxy that answers *every* property with a
 * native method, `then` included — returning or awaiting the plugin itself makes
 * the promise machinery call `SecureStoragePlugin.then()`, which never settles.
 * Compass hung on "Loading…" on its first real phone for exactly that.
 */
let registering: Promise<void> | null = null;
async function registerSecureStore(): Promise<void> {
  registering ??= import("capacitor-secure-storage-plugin").then(
    () => undefined,
    () => undefined,
  );
  await registering;
}

/** The current token, or null. Always null in a browser. */
export function authToken(): string | null {
  return token;
}

/** Fill the cache from encrypted storage, once, before the first request. */
export async function loadAuthToken(): Promise<void> {
  if (!isNative()) return;
  await registerSecureStore();
  const store = secureStore();
  if (!store) return;
  try {
    const { value } = await store.get({ key: TOKEN_KEY });
    token = value || null;
  } catch {
    token = null; // `get` rejects when nothing is stored
  }
}

/** Keep (or, with null, forget) the token. A browser never holds one. */
export async function setAuthToken(next: string | null): Promise<void> {
  if (!isNative()) return;
  token = next;
  await registerSecureStore();
  const store = secureStore();
  if (!store) return;
  try {
    if (next) await store.set({ key: TOKEN_KEY, value: next });
    else await store.remove({ key: TOKEN_KEY });
  } catch {
    /* the memory copy still serves this run */
  }
}

/**
 * Headers the app adds to every API request: what it is, and its token.
 * Empty in a browser, so a web request is exactly what it always was.
 */
export function nativeRequestHeaders(): Record<string, string> {
  if (!isNative()) return {};
  const headers: Record<string, string> = { "X-MyDeck-Client": clientType() };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}
